import { Flex, Slot, Slottable, Spinner, Text } from "@radix-ui/themes";
import { CheckIcon } from "@radix-ui/react-icons";
import { useEffect, useRef, useState } from "react";
import { namespaceClassNames } from "./utils.js";
import { Button, type ButtonProps } from "./elements.js";
import { Translation } from "./i18n/translation.js";

const DONE_TIMEOUT_MS = 1500;
const SAVING_TIMEOUT_MS = 600;

interface SaveButtonProps extends ButtonProps {
  asChild?: boolean;
  children: React.ReactNode;
  loading?: boolean;
  done?: boolean;
  onDone?: () => void;
}

export function SaveButton({
  asChild = false,
  children,
  loading,
  done,
  onDone,
  ...props
}: SaveButtonProps) {
  const [state, setState] = useState<"idle" | "loading" | "done">(
    loading ? "loading" : done ? "done" : "idle",
  );
  const loadingStartTime = useRef<number | null>(null);
  const ButtonComponent = asChild ? Slot : Button;

  useEffect(() => {
    if (loading) {
      // FIXME: This should be refactored
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState("loading");
      loadingStartTime.current = Date.now();
    } else if (done) {
      const currentTime = Date.now();
      const loadingDuration = loadingStartTime.current
        ? currentTime - loadingStartTime.current
        : 0;

      // If loading lasted less than 500 ms, wait for the remaining time
      const remainingDelay = Math.max(0, SAVING_TIMEOUT_MS - loadingDuration);

      let doneTimer: number | null = null;
      const savedTimer = window.setTimeout(() => {
        setState("done");

        doneTimer = window.setTimeout(() => {
          onDone?.();
        }, DONE_TIMEOUT_MS);
      }, remainingDelay);

      return () => {
        window.clearTimeout(savedTimer);
        if (doneTimer !== null) {
          window.clearTimeout(doneTimer);
        }
      };
    } else if (!loading && !done) {
      setState("idle");
    }
  }, [loading, done, onDone]);

  return (
    <ButtonComponent
      {...props}
      className={namespaceClassNames("save-button")}
      data-save-state={state}
    >
      <Slottable>{children}</Slottable>
      {state === "loading" && (
        <Flex
          as="span"
          align="center"
          justify="center"
          position="absolute"
          inset="0"
        >
          <Spinner size="1" />
        </Flex>
      )}

      {state === "done" && (
        <Flex
          as="span"
          align="center"
          justify="center"
          position="absolute"
          inset="0"
          gap="1"
        >
          <CheckIcon
            width="18px"
            height="18px"
            style={{ marginLeft: "-4px" }}
            className={namespaceClassNames("save-button__done-icon")}
          />
          <Text className={namespaceClassNames("save-button__done-text")}>
            <Translation
              defaultMessage="Done"
              id="wjt0K3"
              description="Button label indicating an action has completed successfully"
            />
          </Text>
        </Flex>
      )}
    </ButtonComponent>
  );
}
